// src/services/api.js

/**
 * A helper function to handle fetch requests and responses.
 * @param {string} url - The URL to fetch.
 * @param {object} options - The options for the fetch request.
 * @returns {Promise<object>} - The JSON response from the server.
 */
const fetchApi = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      // Include credentials to send cookies
      credentials: 'include', 
    });

    // console.log('Fetching URL:', url); // Removed debugging line

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'An unknown error occurred.' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    // Handle responses that might not have a body (e.g., 204 No Content for logout)
    const text = await response.text();
    return text ? JSON.parse(text) : {};

  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

/**
 * Registers a new user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} credentials - { email, password, name }.
 * @returns {Promise<object>} - The server response.
 */
export const register = (apiUrl, { email, password, name }) => {
  return fetchApi(`${apiUrl}/api/auth/register`, {
    method: 'POST',
    body: JSON.stringify({ email, password, name }),
  });
};

/**
 * Logs in a user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} credentials - { email, password }.
 * @returns {Promise<object>} - The user profile.
 */
export const login = (apiUrl, { email, password }) => {
  return fetchApi(`${apiUrl}/api/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
};

/**
 * Logs out the current user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<object>} - An empty object on success.
 */
export const logout = (apiUrl) => {
  return fetchApi(`${apiUrl}/api/auth/logout`, {
    method: 'POST',
  });
};

/**
 * Fetches the logs for the authenticated user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} cursor - The cursor for pagination.
 * @param {number} limit - The number of logs to fetch.
 * @param {string} appId - The ID of the app to fetch logs for.
 * @returns {Promise<object>} - The logs, totalCount, totalErrors, totalLogs, totalErrorsLastPeriod, totalLogsLastPeriod and next cursor.
 */
export const getLogs = (apiUrl, { cursor, limit, appId, level, from, to, timeRange }) => {
  const params = new URLSearchParams({ limit });
  if (cursor) {
    params.append('cursor', cursor);
  }
  if (appId) {
    params.append('appId', appId);
  }
  if (level && level !== 'all') { // Only append if a specific level is selected
    params.append('level', level);
  }
  if (from) {
    params.append('from', from);
  }
  if (to) {
    params.append('to', to);
  }
  if (timeRange) {
    params.append('timeRange', timeRange);
  }
  return fetchApi(`${apiUrl}/api/logs?${params.toString()}`);
};

export const getLogBody = (apiUrl, logId) => {
  return fetchApi(`${apiUrl}/api/logs/${logId}/body`);
};

/**
 * Fetches the current authenticated user's profile.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<object>} - The user profile.
 */
export const getMe = (apiUrl) => {
  return fetchApi(`${apiUrl}/api/auth/me`);
};

/**
 * Fetches the apps for the authenticated user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<Array>} - A list of the user's apps.
 */
export const getApps = (apiUrl) => {
    return fetchApi(`${apiUrl}/api/apps`);
};

/**
 * Creates a new app for the authenticated user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} name - The name of the new app.
 * @param {number} ttl - Time-to-live for the initial token in seconds.
 * @returns {Promise<object>} - The newly created app object, including its token.
 */
export const createApp = (apiUrl, name, ttl) => {
    return fetchApi(`${apiUrl}/api/apps`, {
        method: 'POST',
        body: JSON.stringify({ name, ttl }),
    });
};

/**
 * Regenerates the token for a specific app.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} appId - The ID of the app to regenerate the token for.
 * @param {number} ttl - Time-to-live for the new token in seconds.
 * @returns {Promise<object>} - The app object with the new token.
 */
export const regenerateAppToken = (apiUrl, appId, ttl) => {
    return fetchApi(`${apiUrl}/api/apps/${appId}/regenerate-token`, {
        method: 'POST',
        body: JSON.stringify({ ttl }),
    });
};

/**
 * Fetches all lighthouses for the user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<Array>} - A list of the user's lighthouses.
 */
export const getLighthouses = (apiUrl) => {
    return fetchApi(`${apiUrl}/api/lighthouses`);
};

/**
 * Creates a new lighthouse.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} lighthouseData - { name, url, interval }.
 * @returns {Promise<object>} - The newly created lighthouse.
 */
export const createLighthouse = (apiUrl, lighthouseData) => {
    return fetchApi(`${apiUrl}/api/lighthouses`, {
        method: 'POST',
        body: JSON.stringify(lighthouseData),
    });
};

/**
 * Deletes a lighthouse.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} lighthouseId - The ID of the lighthouse to delete.
 * @returns {Promise<object>} - A confirmation message.
 */
export const deleteLighthouse = (apiUrl, lighthouseId) => {
    return fetchApi(`${apiUrl}/api/lighthouses/${lighthouseId}`, {
        method: 'DELETE',
    });
};

/**
 * Fetches the ping history for a specific lighthouse.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} lighthouseId - The ID of the lighthouse.
 * @returns {Promise<Array>} - A list of ping records.
 */
export const getLighthousePings = (apiUrl, lighthouseId) => {
    return fetchApi(`${apiUrl}/api/lighthouses/${lighthouseId}/pings`);
};

/**
 * Fetches all tokens for a specific app.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} appId - The ID of the app.
 * @returns {Promise<object>} - Object containing array of tokens.
 */
export const getAppTokens = (apiUrl, appId) => {
    return fetchApi(`${apiUrl}/api/apps/${appId}/tokens`);
};

/**
 * Creates a new token for a specific app.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} appId - The ID of the app.
 * @param {object} data - { name, ttl }.
 * @returns {Promise<object>} - The newly created token object.
 */
export const createAppToken = (apiUrl, appId, { name, ttl }) => {
    return fetchApi(`${apiUrl}/api/apps/${appId}/tokens`, {
        method: 'POST',
        body: JSON.stringify({ name, ttl }),
    });
};

/**
 * Deletes a token for a specific app.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} appId - The ID of the app.
 * @param {string} tokenId - The ID of the token to delete.
 * @returns {Promise<object>} - Success message.
 */
export const deleteAppToken = (apiUrl, appId, tokenId) => {
    return fetchApi(`${apiUrl}/api/apps/${appId}/tokens/${tokenId}`, {
        method: 'DELETE',
    });
};

/**
 * Updates an app's settings.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} appId - The ID of the app to update.
 * @param {object} data - { name }.
 * @returns {Promise<object>} - The updated app object.
 */
export const updateApp = (apiUrl, appId, { name }) => {
    return fetchApi(`${apiUrl}/api/apps/${appId}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
    });
};

/**
 * Deletes an app and all its associated data.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} appId - The ID of the app to delete.
 * @returns {Promise<object>} - Success message.
 */
export const deleteApp = (apiUrl, appId) => {
    return fetchApi(`${apiUrl}/api/apps/${appId}`, {
        method: 'DELETE',
    });
};

/**
 * Updates user profile information.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} data - { name?, email? }.
 * @returns {Promise<object>} - The updated user object.
 */
export const updateProfile = (apiUrl, data) => {
    return fetchApi(`${apiUrl}/api/auth/profile`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
};

/**
 * Changes user password.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} data - { currentPassword, newPassword }.
 * @returns {Promise<object>} - Success message.
 */
export const changePassword = (apiUrl, { currentPassword, newPassword }) => {
    return fetchApi(`${apiUrl}/api/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
    });
};

/**
 * Deletes the user account and all associated data.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<object>} - Success message.
 */
export const deleteAccount = (apiUrl) => {
    return fetchApi(`${apiUrl}/api/auth/account`, {
        method: 'DELETE',
    });
};

/**
 * Initiates the password recovery process by sending a reset email.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {string} email - The email address for password reset.
 * @returns {Promise<object>} - Success message.
 */
export const forgotPassword = (apiUrl, email) => {
    return fetchApi(`${apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
};

/**
 * Resets the user's password with a new password and a reset token.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} data - { token, newPassword }.
 * @returns {Promise<object>} - Success message.
 */
export const resetPassword = (apiUrl, { token, newPassword }) => {
    return fetchApi(`${apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
    });
};

/**
 * Fetches all available subscription plans.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<Array>} - A list of subscription plans.
 */
export const getSubscriptionPlans = (apiUrl) => {
    return fetchApi(`${apiUrl}/api/subscription/plans`);
};

/**
 * Fetches the current authenticated user's subscription.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<object>} - The user's subscription object.
 */
export const getUserSubscription = (apiUrl) => {
    return fetchApi(`${apiUrl}/api/subscription/me`);
};

/**
 * Creates a new subscription for the authenticated user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} data - { plan_id }.
 * @returns {Promise<object>} - The newly created subscription object.
 */
export const createSubscription = (apiUrl, { plan_id }) => {
    return fetchApi(`${apiUrl}/api/subscription`, {
        method: 'POST',
        body: JSON.stringify({ plan_id }),
    });
};

/**
 * Updates an existing subscription for the authenticated user.
 * @param {string} apiUrl - The base URL of the worker API.
 * @param {object} data - { plan_id }.
 * @returns {Promise<object>} - The updated subscription object.
 */
export const updateSubscription = (apiUrl, { plan_id }) => {
    return fetchApi(`${apiUrl}/api/subscription`, {
        method: 'PUT',
        body: JSON.stringify({ plan_id }),
    });
};

/**
 * Cancels the authenticated user's subscription.
 * @param {string} apiUrl - The base URL of the worker API.
 * @returns {Promise<object>} - Success message.
 */
export const cancelSubscription = (apiUrl) => {
    return fetchApi(`${apiUrl}/api/subscription`, {
        method: 'DELETE',
    });
};
